/**
 * @fileoverview Breadcrumbs component React interface.
 * @author Emily Zhang (ezhang@yext.com)
 */
goog.declareModuleId('yext.ui.components.Breadcrumbs');


import {BreadcrumbShapeJsDoc} from '/ui/components/breadcrumbs/breadcrumbshape';
import {DeprecatedOldButton as Button} from '/ui/components/button/button';
import {MenuController} from '/ui/components/menu/menucontroller';
import {IconTooltip} from '/ui/components/tooltip/tooltip';
import {hasManaUITheme} from '/ui/design/hasManaUITheme';
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/breadcrumbs/breadcrumbs.module.scss';

/**
 * Renders the breadcrumbs
 *
 * @param {{
 *   breadcrumbsList: Array<BreadcrumbShapeJsDoc>,
 *   pendoId?: string,
 * }} props
 * @returns {React.ReactElement}
 */
export function Breadcrumbs(props) {
  return (
    <nav aria-label={yext.msg('breadcrumbs')} data-pendo={props.pendoId}>
      <ol className={styles.list}>
        {props.breadcrumbsList.map(renderSingleBreadcrumb)}
      </ol>
    </nav>
  );
}

function renderSingleBreadcrumb(breadcrumb, index, breadcrumbsList) {
  const isLastElement = index === breadcrumbsList.length - 1;
  let renamedLateralNavOptions = [];
  let breadcrumbMarkup = undefined;

  if (breadcrumb.lateralNavOptions) {
    // MenuController actions require names with object key `name` and URLs with object key `href`,
    // but Breadcrumb nodes require names with object key `displayName` and URLs with object key `link`.
    // We rename `displayName` to `name` and `link` to `href` for each lateralNavOption for consistency with Breadcrumb nodes.
    renamedLateralNavOptions = breadcrumb.lateralNavOptions.map(({displayName: name, link: href, pendoId: pendoId}) => ({name, href, pendoId}));
  }

  if (breadcrumb.lateralNavOptions && isLastElement) {
    // Any breadcrumb node with lateralNavOptions will have its `link` and `onClick` props ignored.
    const penultimateBreadcrumb = breadcrumbsList.length >= 2 ? breadcrumbsList[breadcrumbsList.length - 2] : null;
    let parentNodeName = '';
    if (penultimateBreadcrumb) {
      parentNodeName = penultimateBreadcrumb.displayName;
    }

    const tooltipText = penultimateBreadcrumb
      ? yext.msg('Navigate to another {0} page', parentNodeName)
      : yext.msg('Navigate to another page');

    breadcrumbMarkup = (
      <span className={styles.breadcrumbWithDropdown}>
        <span>{breadcrumb.displayName}</span>
        <IconTooltip
          element={
            <span>
              <MenuController
                actions={renamedLateralNavOptions}
                dropdownTriggerClass={styles.dropdown}
              />
            </span>
          }
          tooltipText={tooltipText}
          direction="top"
        />
      </span>
    );
  } else if (breadcrumb.onClick) {
    breadcrumbMarkup = (
      <Button
        chromeless={true}
        onClick={() => {
          breadcrumb.onClick();
          return false;
        }}
        pendoId={breadcrumb.pendoId}
      >
        {breadcrumb.displayName}
      </Button>
    );
  } else if (breadcrumb.link) {
    breadcrumbMarkup = (
      <a
        href={breadcrumb.link}
        aria-current={isLastElement ? 'location' : undefined}
        data-pendo={breadcrumb.pendoId}
        className={hasManaUITheme() ? styles.manaBreadcrumb : undefined}
      >
        {breadcrumb.displayName}
      </a>
    );
  } else {
    breadcrumbMarkup = (
      <span className={hasManaUITheme() ? styles.manaLastCrumb : undefined}>
        {breadcrumb.displayName}
      </span>
    );
  }

  return (
    <li key={breadcrumb.displayName} className={styles.breadcrumb}>
      {breadcrumbMarkup}
      {!isLastElement && (
        <span aria-hidden>&nbsp;/&nbsp;</span>
      )}
    </li>
  );
}
